import type { GboxDocument } from '@gotti/sandbox-shared';

/** What opening the sandbox the URL names needs from the page. */
export interface OpenFromUrlDeps {
  /** Loads the document the URL names (`useSandboxPersistence`), or gives null when there is none. */
  loadFromUrl: () => Promise<GboxDocument | null>;
  /** Puts the document in the editor; throws when this site cannot open it. */
  open: (doc: GboxDocument) => void;
  /** Forgets the loaded sandbox, so a Save makes a new one instead of a revision of a sandbox the editor never showed. */
  detach: () => void;
  /** Tells the person why — an alert in the page. */
  report: (message: string) => void;
}

/**
 * Opens the sandbox the URL names, returning the document it opened, or null. A document the editor cannot open is
 * reported to the person, never dropped silently, and the sandbox is forgotten: the editor still shows the default
 * template, and a Save that appended it as the sandbox's newest revision would bury the person's work under it. The
 * URL stays as it is, so a reload with a newer site can open it.
 */
export async function openSandboxFromUrl({ loadFromUrl, open, detach, report }: OpenFromUrlDeps): Promise<GboxDocument | null> {
  const doc = await loadFromUrl();
  if (!doc) return null;
  try {
    open(doc);
    return doc;
  } catch (err) {
    console.error('[Sandbox] Could not open the sandbox this URL names:', err);
    detach();
    report(`“${doc.meta?.name ?? 'This sandbox'}” could not be opened: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}
